import { useState } from 'react';
import type { FormEvent } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import { WEBHOOK_EVENT_NAMES, WebhookDelivery, WebhookDeliveryDetail, WebhookEndpoint, WebhookEndpointWithSecret, page } from '@cloudsign/shared';
import type { WebhookEndpointCreate, WebhookEventName } from '@cloudsign/shared';
import { z } from 'zod';
import { Alert, Badge, Button, Card, CheckboxField, ConfirmButton, EmptyRow, FilterBar, LinkButton, PageHeader, Pager, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { SecretDialog, StatusPill } from '../../components/phase2';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { endpointsKey, useEndpoints } from './api';
import { SitePicker } from './Pickers';

/** `test` is sent by the Send test button, never chosen. */
const CHOOSABLE = WEBHOOK_EVENT_NAMES.filter((e) => e !== 'test');
const DeliveryPage = page(WebhookDelivery);
const TestResult = z.object({ ok: z.boolean(), statusCode: z.number().int().nullable(), errorKey: z.string().nullable(), durationMs: z.number().int() });
const BUTTON_LINK = 'inline-flex min-h-10 items-center rounded-md border border-slate-300 bg-white px-3.5 text-sm font-medium hover:bg-slate-50 sm:min-h-9';

const ENDPOINT_TONE = { active: 'good', paused: 'neutral', failing: 'bad' } as const;
const DELIVERY_TONE = { queued: 'warn', sending: 'info', retrying: 'warn', succeeded: 'good', abandoned: 'bad' } as const;

export function EndpointStatus({ status }: { status: WebhookEndpoint['status'] }) {
  return <StatusPill tone={ENDPOINT_TONE[status]}>{t(`whk.status.${status}`)}</StatusPill>;
}

export function WebhooksPage() {
  const q = useEndpoints();
  const canManage = useCan('webhooks.manage');
  return (
    <div>
      <PageHeader
        title={t('whk.title')}
        description={t('whk.description')}
        actions={
          canManage ? (
            <LinkButton to="/manage/webhooks/new" variant="primary">
              {t('whk.add')}
            </LinkButton>
          ) : undefined
        }
      />
      {q.isError ? <ErrorPanel error={q.error} onRetry={() => void q.refetch()} /> : null}
      {!q.data && !q.isError ? <Spinner /> : null}
      {q.data ? (
        <TableWrap caption={t('whk.caption')}>
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <Th>{t('common.name')}</Th>
              <Th>{t('whk.url')}</Th>
              <Th>{t('whk.events')}</Th>
              <Th>{t('common.status')}</Th>
              <Th>{t('whk.lastSuccess')}</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {q.data.length === 0 ? (
              <EmptyRow
                cols={5}
                action={
                  canManage ? (
                    <LinkButton to="/manage/webhooks/new" variant="primary">
                      {t('whk.add')}
                    </LinkButton>
                  ) : undefined
                }
              />
            ) : null}
            {q.data.map((e) => (
              <tr key={e.id}>
                <td className="px-4 py-3 font-medium">
                  <Link className="text-brand hover:underline" to={`/manage/webhooks/${e.id}`}>
                    {e.name}
                  </Link>{' '}
                  {e.ownerModule ? <Badge>{t('whk.managed')}</Badge> : null}
                </td>
                <td className="max-w-xs truncate px-4 py-3 text-slate-600" title={e.url}>
                  {e.url}
                </td>
                <td className="px-4 py-3">{t('whk.eventsCount', { count: e.events.length })}</td>
                <td className="px-4 py-3">
                  <EndpointStatus status={e.status} />
                </td>
                <td className="px-4 py-3">{e.lastSuccessAt ? formatDateTime(e.lastSuccessAt) : t('whk.never')}</td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      ) : null}
    </div>
  );
}

interface Draft {
  name: string;
  url: string;
  events: WebhookEventName[];
  siteIds: string[] | null;
  skipSources: string;
  ordered: boolean;
  payloadProfile: 'standard' | 'minimal';
  alertAfterMinutes: number;
  alertEmail: string;
}

const BLANK: Draft = { name: '', url: '', events: [], siteIds: null, skipSources: '', ordered: false, payloadProfile: 'standard', alertAfterMinutes: 60, alertEmail: '' };

function fromEndpoint(e: WebhookEndpoint): Draft {
  return {
    name: e.name,
    url: e.url,
    events: e.events,
    siteIds: e.siteIds,
    skipSources: e.skipSources.join(', '),
    ordered: e.ordered,
    payloadProfile: e.payloadProfile,
    alertAfterMinutes: e.alertAfterMinutes,
    alertEmail: e.alertEmail ?? '',
  };
}

function toBody(d: Draft): WebhookEndpointCreate {
  return {
    name: d.name.trim(),
    url: d.url.trim(),
    events: d.events,
    siteIds: d.siteIds,
    skipSources: d.skipSources.split(/[,\s]+/).filter(Boolean),
    ordered: d.ordered,
    payloadProfile: d.payloadProfile,
    alertAfterMinutes: d.alertAfterMinutes,
    alertEmail: d.alertEmail.trim() || null,
  } as WebhookEndpointCreate;
}

function EndpointForm({ initial, readOnly, submitLabel, pending, error, onSubmit }: { initial: Draft; readOnly: boolean; submitLabel: string; pending: boolean; error: unknown; onSubmit: (d: Draft) => void }) {
  const [d, setD] = useState<Draft>(initial);
  const [problems, setProblems] = useState<Record<string, string>>({});
  const set = (patch: Partial<Draft>) => setD((x) => ({ ...x, ...patch }));
  const serverErrors = fieldErrors(error);
  const err = (k: string) => problems[k] ?? serverErrors[k];
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const found: Record<string, string> = {};
    if (!d.name.trim()) found['name'] = t('whk.form.nameRequired');
    if (!/^https?:\/\/\S+$/i.test(d.url.trim())) found['url'] = t('whk.form.urlInvalid');
    if (d.events.length === 0) found['events'] = t('whk.form.eventsRequired');
    if (d.siteIds !== null && d.siteIds.length === 0) found['siteIds'] = t('ntf.rule.pickSite');
    setProblems(found);
    if (Object.keys(found).length === 0) onSubmit(d);
  };
  const toggleEvent = (ev: WebhookEventName, on: boolean) => set({ events: on ? [...d.events, ev] : d.events.filter((x) => x !== ev) });
  return (
    <form onSubmit={submit} noValidate className="space-y-6">
      <Card title={t('whk.form.endpoint')}>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label={t('common.name')} value={d.name} maxLength={100} disabled={readOnly} onChange={(e) => set({ name: e.target.value })} error={err('name')} />
          <TextField label={t('whk.url')} type="url" value={d.url} maxLength={2000} disabled={readOnly} hint={t('whk.form.urlHint')} onChange={(e) => set({ url: e.target.value })} error={err('url')} />
        </div>
      </Card>
      <Card title={t('whk.events')} description={t('whk.form.eventsHint')}>
        <fieldset aria-describedby={err('events') ? 'whk-events-err' : undefined}>
          <legend className="sr-only">{t('whk.events')}</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {CHOOSABLE.map((ev) => (
              <CheckboxField key={ev} label={t(`whk.event.${ev}`)} checked={d.events.includes(ev)} disabled={readOnly} onChange={(e) => toggleEvent(ev, e.target.checked)} />
            ))}
          </div>
          {err('events') ? (
            <p id="whk-events-err" className="mt-2 text-sm text-red-700">
              {err('events')}
            </p>
          ) : null}
        </fieldset>
      </Card>
      <Card title={t('whk.form.options')}>
        <div className="space-y-4">
          <SitePicker value={d.siteIds} onChange={(siteIds) => set({ siteIds })} disabled={readOnly} error={err('siteIds')} />
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField label={t('whk.form.profile')} value={d.payloadProfile} disabled={readOnly} hint={t('whk.form.profileHint')} onChange={(e) => set({ payloadProfile: e.target.value as Draft['payloadProfile'] })}>
              <option value="standard">{t('whk.profile.standard')}</option>
              <option value="minimal">{t('whk.profile.minimal')}</option>
            </SelectField>
            <TextField label={t('whk.form.skipSources')} value={d.skipSources} disabled={readOnly} hint={t('whk.form.skipSourcesHint')} onChange={(e) => set({ skipSources: e.target.value })} />
            <TextField
              label={t('whk.form.alertAfter')}
              type="number"
              min={0}
              max={10080}
              value={d.alertAfterMinutes}
              disabled={readOnly}
              hint={t('whk.form.alertAfterHint')}
              onChange={(e) => set({ alertAfterMinutes: Number(e.target.value) })}
            />
            <TextField label={t('whk.form.alertEmail')} type="email" value={d.alertEmail} disabled={readOnly} hint={t('whk.form.alertEmailHint')} onChange={(e) => set({ alertEmail: e.target.value })} error={err('alertEmail')} />
          </div>
          <CheckboxField label={t('whk.form.ordered')} hint={t('whk.form.orderedHint')} checked={d.ordered} disabled={readOnly} onChange={(e) => set({ ordered: e.target.checked })} />
        </div>
      </Card>
      <ErrorLine error={error} />
      {readOnly ? null : (
        <div className="flex justify-end">
          <Button type="submit" variant="primary" disabled={pending}>
            {submitLabel}
          </Button>
        </div>
      )}
    </form>
  );
}

export function WebhookCreatePage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [created, setCreated] = useState<WebhookEndpointWithSecret | null>(null);
  const create = useMutation({
    mutationFn: (d: Draft) => api.post('/webhooks/endpoints', { body: toBody(d), schema: WebhookEndpointWithSecret }),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: endpointsKey });
      setCreated(r);
    },
  });
  return (
    <div>
      <PageHeader
        title={t('whk.new')}
        description={t('whk.newDescription')}
        actions={
          <Link to="/manage/webhooks" className={BUTTON_LINK}>
            {t('common.back')}
          </Link>
        }
      />
      <EndpointForm initial={BLANK} readOnly={false} submitLabel={t('whk.create')} pending={create.isPending} error={create.error} onSubmit={(d) => create.mutate(d)} />
      {created ? (
        <SecretDialog
          title={t('whk.secret.title')}
          description={t('whk.secret.description')}
          secret={created.secret}
          secretLabel={t('whk.secret.label')}
          onClose={() => {
            const id = created.endpoint.id;
            setCreated(null);
            void nav(`/manage/webhooks/${id}`);
          }}
        />
      ) : null}
    </div>
  );
}

export function WebhookDetailPage() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const canManage = useCan('webhooks.manage');
  const canDelete = useCan('webhooks.delete');
  const [secret, setSecret] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<z.infer<typeof TestResult> | null>(null);
  const q = useQuery({ queryKey: [...endpointsKey, id], queryFn: () => api.get(`/webhooks/endpoints/${id}`, { schema: WebhookEndpoint }) });
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: endpointsKey })]);
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/webhooks/endpoints/${id}`, { body, schema: WebhookEndpoint }),
    onSuccess: async () => {
      await refresh();
      toast(t('common.saved'));
    },
  });
  const rotate = useMutation({
    mutationFn: () => api.post(`/webhooks/endpoints/${id}/rotate-secret`, { schema: WebhookEndpointWithSecret }),
    onSuccess: async (r) => {
      await refresh();
      setSecret(r.secret);
    },
  });
  const test = useMutation({
    mutationFn: () => api.post(`/webhooks/endpoints/${id}/test`, { schema: TestResult }),
    onSuccess: async (r) => {
      setTestResult(r);
      await qc.invalidateQueries({ queryKey: ['webhooks', 'deliveries'] });
    },
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/webhooks/endpoints/${id}`),
    onSuccess: async () => {
      await refresh();
      void nav('/manage/webhooks');
    },
  });
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Spinner />;
  const e = q.data;
  const managed = e.ownerModule !== null;
  const readOnly = managed || !canManage;
  return (
    <div className="space-y-6">
      <PageHeader
        title={e.name}
        description={t('whk.detailDescription', { hint: e.secretHint })}
        actions={
          <Link to="/manage/webhooks" className={BUTTON_LINK}>
            {t('common.back')}
          </Link>
        }
      />
      {managed ? <Alert kind="info">{t('whk.managedNote', { module: e.ownerModule ?? '' })}</Alert> : null}
      <Card title={t('common.status')}>
        <div className="flex flex-wrap items-center gap-3">
          <EndpointStatus status={e.status} />
          {e.failingSince ? <span className="text-sm text-slate-600">{t('whk.failingSince', { when: formatDateTime(e.failingSince) })}</span> : null}
          {e.lastSuccessAt ? <span className="text-sm text-slate-600">{t('whk.lastSuccessAt', { when: formatDateTime(e.lastSuccessAt) })}</span> : null}
        </div>
        {!readOnly ? (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button disabled={save.isPending} onClick={() => save.mutate({ status: e.status === 'paused' ? 'active' : 'paused' })}>
              {e.status === 'paused' ? t('whk.resume') : t('whk.pause')}
            </Button>
            <Button disabled={test.isPending} onClick={() => test.mutate()}>
              {t('whk.sendTest')}
            </Button>
            <ConfirmButton label={t('whk.rotate')} confirmLabel={t('whk.rotateConfirm')} pending={rotate.isPending} onConfirm={() => rotate.mutate()} />
            {canDelete ? <ConfirmButton label={t('common.delete')} pending={remove.isPending} onConfirm={() => remove.mutate()} /> : null}
          </div>
        ) : null}
        <div className="mt-3 space-y-2" aria-live="polite">
          <ErrorLine error={rotate.error ?? test.error ?? remove.error} />
          {testResult ? (
            <Alert kind={testResult.ok ? 'success' : 'error'}>
              {testResult.ok
                ? t('whk.test.ok', { code: testResult.statusCode ?? 0, ms: testResult.durationMs })
                : t('whk.test.failed', { reason: testResult.errorKey ? t(testResult.errorKey) : String(testResult.statusCode ?? '') })}
            </Alert>
          ) : null}
        </div>
      </Card>
      <EndpointForm key={e.updatedAt} initial={fromEndpoint(e)} readOnly={readOnly} submitLabel={t('common.save')} pending={save.isPending} error={save.error} onSubmit={(d) => save.mutate(toBody(d))} />
      <DeliveryLog endpointId={id} canManage={canManage} />
      {secret ? (
        <SecretDialog title={t('whk.rotated.title')} description={t('whk.rotated.description')} secret={secret} secretLabel={t('whk.secret.label')} onClose={() => setSecret(null)} />
      ) : null}
    </div>
  );
}

const DELIVERY_STATUSES = ['queued', 'sending', 'retrying', 'succeeded', 'abandoned'] as const;

function DeliveryLog({ endpointId, canManage }: { endpointId: string; canManage: boolean }) {
  const [pageNo, setPageNo] = useState(1);
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const query = { endpointId, page: pageNo, pageSize: 20, status: status || undefined };
  const q = useQuery({
    queryKey: ['webhooks', 'deliveries', query],
    placeholderData: keepPreviousData,
    queryFn: () => api.get('/webhooks/deliveries', { query, schema: DeliveryPage }),
  });
  return (
    <section aria-labelledby="whk-log">
      <h2 id="whk-log" className="mb-3 text-lg font-semibold text-slate-900">
        {t('whk.log.title')}
      </h2>
      <FilterBar columns="sm:grid-cols-3">
        <SelectField
          label={t('common.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPageNo(1);
          }}
        >
          <option value="">{t('common.all')}</option>
          {DELIVERY_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`whk.delivery.${s}`)}
            </option>
          ))}
        </SelectField>
      </FilterBar>
      {q.isError ? <ErrorPanel error={q.error} onRetry={() => void q.refetch()} /> : null}
      {!q.data && !q.isError ? <Spinner /> : null}
      {q.data ? (
        <>
          <TableWrap caption={t('whk.log.caption')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>{t('ntf.log.when')}</Th>
                <Th>{t('whk.log.event')}</Th>
                <Th>{t('common.status')}</Th>
                <Th>{t('ntf.log.attempts')}</Th>
                <Th>{t('whk.log.code')}</Th>
                <Th>
                  <span className="sr-only">{t('common.actions')}</span>
                </Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {q.data.items.length === 0 ? <EmptyRow cols={6} /> : null}
              {q.data.items.map((d) => (
                <DeliveryRows key={d.id} delivery={d} open={open === d.id} onToggle={() => setOpen(open === d.id ? null : d.id)} canManage={canManage} />
              ))}
            </tbody>
          </TableWrap>
          <Pager page={q.data.page} pageSize={q.data.pageSize} total={q.data.total} onPage={setPageNo} />
        </>
      ) : null}
    </section>
  );
}

function DeliveryRows({ delivery: d, open, onToggle, canManage }: { delivery: WebhookDelivery; open: boolean; onToggle: () => void; canManage: boolean }) {
  return (
    <>
      <tr>
        <td className="whitespace-nowrap px-4 py-3">{formatDateTime(d.occurredAt)}</td>
        <td className="px-4 py-3">{t(`whk.event.${d.type}`)}</td>
        <td className="px-4 py-3">
          <StatusPill tone={DELIVERY_TONE[d.status]}>{t(`whk.delivery.${d.status}`)}</StatusPill>
          {d.nextAttemptAt && d.status === 'retrying' ? <p className="mt-1 text-xs text-slate-600">{t('whk.log.nextTry', { when: formatDateTime(d.nextAttemptAt) })}</p> : null}
        </td>
        <td className="px-4 py-3">{d.attempts}</td>
        <td className="px-4 py-3">{d.lastStatusCode ?? ''}</td>
        <td className="px-4 py-3">
          <Button aria-expanded={open} onClick={onToggle}>
            {open ? t('whk.log.hide') : t('whk.log.details')}
            <span className="sr-only"> {formatDateTime(d.occurredAt)}</span>
          </Button>
        </td>
      </tr>
      {open ? (
        <tr>
          <td colSpan={6} className="bg-slate-50 px-4 py-4">
            <DeliveryDetail id={d.id} canManage={canManage} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function DeliveryDetail({ id, canManage }: { id: string; canManage: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['webhooks', 'delivery', id], queryFn: () => api.get(`/webhooks/deliveries/${id}`, { schema: WebhookDeliveryDetail }) });
  const resend = useMutation({
    mutationFn: () => api.post(`/webhooks/deliveries/${id}/retry`, { schema: WebhookDelivery }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['webhooks'] });
      toast(t('whk.log.resent'));
    },
  });
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Spinner />;
  const d = q.data;
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-slate-900">{t('whk.log.attempts')}</h3>
      {d.attemptLog.length === 0 ? (
        <p className="text-sm text-slate-600">{t('whk.log.noAttempts')}</p>
      ) : (
        <ol className="space-y-2">
          {d.attemptLog.map((a) => (
            <li key={a.attempt} className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="font-medium">{t('whk.log.attemptNumber', { n: a.attempt })}</span>
                <span>{formatDateTime(a.at)}</span>
                <span>{a.statusCode !== null ? t('whk.log.statusCode', { code: a.statusCode }) : a.errorKey ? t(a.errorKey) : ''}</span>
                <span className="text-slate-600">{t('whk.log.duration', { ms: a.durationMs })}</span>
              </div>
              {a.responseExcerpt ? <pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded bg-slate-50 p-2 text-xs">{a.responseExcerpt}</pre> : null}
            </li>
          ))}
        </ol>
      )}
      <div>
        <h3 className="mb-1 text-sm font-semibold text-slate-900">{t('whk.log.payload')}</h3>
        {d.payload === null ? <p className="text-sm text-slate-600">{t('whk.log.payloadCleared')}</p> : <pre className="max-h-64 overflow-auto rounded bg-white p-3 text-xs ring-1 ring-slate-200">{JSON.stringify(d.payload, null, 2)}</pre>}
      </div>
      <ErrorLine error={resend.error} />
      {canManage && (d.status === 'abandoned' || d.status === 'succeeded') ? (
        <Button disabled={resend.isPending} onClick={() => resend.mutate()}>
          {t('whk.log.resend')}
        </Button>
      ) : null}
    </div>
  );
}
